import { EmeraldError } from 'src/common/api/emerald-error';
import { AbortedError } from 'src/common/api/extended-emerald-error';
import { showEmeraldSelectionDialog } from 'src/common/components/interactive-dialogue/components/selection-dialogue/selection-dialog';
import { SelectListItem } from 'src/common/components/select-list/select-list';
import { popEmeraldModal } from 'src/common/emerald-modal-manager/emerald-modal-manager';
import { ResourceNotFound } from 'src/common/helpers/error-code';
import { store } from 'src/common/redux/configure-store';
import { resetCardData } from 'src/devices/payment/get-card-details';
import { addItemToTransaction } from 'src/home-page/business-logic/sale-mode-router';
import { dataAdd } from 'src/home-page/business-logic/service/data-add';
import { voidEmptyTransaction } from 'src/home-page/business-logic/void-transaction';
import {
  getProductListings,
  IGetProductListingsResponse,
} from 'src/items-list/business-logic/service/product-lookup';
import { popupAsyncMessage } from 'src/messages/business-logic/popup-message';
import {
  OnlineItemServiceType,
  SellingAdditionalData,
} from 'src/online-items/dto/CartItemActivation';
import { EntryMethod } from './../../common/enumerations/entry-method';
import {
  cancelPinpadOperation,
  endPaymentSession,
} from './../../devices/payment/pinpad-operations';

const getOnlineItemId = async (items: IGetProductListingsResponse): Promise<string> => {
  const selectListItems = items.productListings.map(
    (product): SelectListItem => ({
      id: product.itemId || '',
      description: product.description,
    }),
  );
  return new Promise<string>((resolve, reject) => {
    showEmeraldSelectionDialog({
      selectListItems,
      message: 'ReloadItemSelectItemMessage',
      subMessage: 'ReloadItemSelectItemSubMessage',
      onCancel: () => reject(new AbortedError()),
      onAccept: id => resolve(id),
      dataTestId: 'reload-item-select-id',
    });
  }).finally(() => {
    popEmeraldModal();
  });
};

const handleError = async (e: any, voidOnError: boolean): Promise<void> => {
  if (e.code === ResourceNotFound) {
    await popupAsyncMessage({
      message: 'EmeraldItemNotFound',
    });
  } else {
    await popupAsyncMessage(e);
  }
  if (voidOnError) {
    await voidEmptyTransaction();
  }
};

export const handleReloadItemEntry = async (
  data: string,
  entryMethod: EntryMethod,
  voidOnError: boolean,
): Promise<void> => {
  const transactionId = store.getState().transaction.transactionId;
  let itemId: string | undefined;
  let groupId: string | undefined;
  let onlineExternalId: string | undefined;

  if (entryMethod !== EntryMethod.MSR) {
    await cancelPinpadOperation();
  } else {
    await endPaymentSession();
    resetCardData();
  }

  try {
    const decodeData = await dataAdd(transactionId, {
      itemId: data,
    });
    itemId = decodeData.itemId;
    if (entryMethod === EntryMethod.MSR) {
      onlineExternalId = data;
    }
    onlineExternalId = decodeData.onlineItemExternalId ?? onlineExternalId;
    groupId = decodeData.groupId;
  } catch (error) {
    // With a plain old item this returns an error, so let's do the old college try
    itemId = data;
  }

  try {
    if (!itemId && groupId) {
      const items = await getProductListings({ groupId });
      if (items.total > 1) {
        itemId = await getOnlineItemId(items);
      } else if (items.total === 1) {
        const [firstItem] = items.productListings;
        itemId = firstItem.itemId;
      } else {
        throw new EmeraldError('EmeraldItemNotFound');
      }
    } else if (!itemId) {
      throw new EmeraldError('EmeraldItemNotFound');
    }
  } catch (e) {
    await handleError(e, voidOnError);
  }

  if (itemId) {
    const additionalData: SellingAdditionalData = {
      onlineItem: {
        serviceType: OnlineItemServiceType.Reload,
        authorizationExternalId: onlineExternalId,
        externalIdEntryMethod: entryMethod,
      },
    };

    const inputData = { itemId, additionalData };

    await addItemToTransaction(inputData).catch(async e => {
      await handleError(e, voidOnError);
    });
  }
};
